/**
 * The Stats page's request. Every number is computed on the server by
 * `stats_logic`, over the same filtered rows the Events page lists.
 */
import { authedFetch } from "./api";
import { filtersToSearch, type FilterOptions, type Filters } from "./eventsPage";
import { orThrow } from "./http";

export interface Record3 {
  gp: number;
  w: number;
  l: number;
  t: number;
}

export interface VenueDrill {
  name: string;
  city: string | null;
  image: string | null;
  games: number;
  teams_seen: [string, number][];
}

export interface StatsDto {
  total: number;
  shown: number;
  leagues: number;
  venues: number;
  years: number;
  /** The filtered team, else the favored team. */
  team: string;
  team_logo: string | null;
  record: { overall: Record3; home: Record3; away: Record3 };
  top_venues: [string, number][];
  /** Set when a single venue is filtered: its drill-down replaces Top venues. */
  venue: VenueDrill | null;
  by_league: { league: string; sport: string; emoji: string; games: number }[];
  by_year: [string, number][];
  options: FilterOptions;
}

export async function fetchStats(f: Filters): Promise<StatsDto> {
  const qs = filtersToSearch(f).toString();
  const res = await authedFetch(`/api/stats${qs ? `?${qs}` : ""}`);
  return (await orThrow(res, "Could not load stats")) as StatsDto;
}

/** "33-21-4": wins, losses, and ties only when there are any. */
export function recordText(r: Record3): string {
  return `${r.w}-${r.l}${r.t ? `-${r.t}` : ""}`;
}

/** "61.1% wins", or a plain note when nothing final matched. */
export function winPct(r: Record3): string {
  return r.gp ? `${((r.w / r.gp) * 100).toFixed(1)}% wins` : "no final scores";
}

/** Every year from the first to the last, zero-filled, so gaps show as gaps. */
export function yearSeries(byYear: [string, number][]): [string, number][] {
  if (byYear.length === 0) return [];
  const counts = new Map(byYear);
  const years = byYear.map(([y]) => Number(y));
  const out: [string, number][] = [];
  for (let y = Math.min(...years); y <= Math.max(...years); y++) out.push([String(y), counts.get(String(y)) ?? 0]);
  return out;
}
